import {
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import {
  TooltipBase,
  TooltipContainer,
  TooltipTrigger,
} from '@mentimeter/ragnar-ui/tooltip';

// Delay for enabling the tooltip after a modal has been closed
// This has to be higher than the time it takes for the modal to close
// and focus is returned to the trigger.
const TOOLTIP_DELAY = 350;

interface HeaderTriggerWithTooltipProps {
  modalOpen: boolean;
  trigger: ReactNode;
}

export const HeaderTriggerWithTooltip = ({
  modalOpen,
  trigger,
  children,
}: PropsWithChildren<HeaderTriggerWithTooltipProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suppressTooltip, setSuppressTooltip] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (modalOpen) {
      setIsOpen(false);
      setSuppressTooltip(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        setSuppressTooltip(false);
      }, TOOLTIP_DELAY);
    }
  }, [modalOpen]);

  const handleTooltipOpenChange = (open: boolean) => {
    if (!suppressTooltip) {
      setIsOpen(open);
    }
  };

  return (
    <TooltipBase open={isOpen} onOpenChange={handleTooltipOpenChange}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContainer>{children}</TooltipContainer>
    </TooltipBase>
  );
};
